import { proStudioUrqlGql } from '@pypestream/api-services';
import { useEffect } from 'react';

import { isCollabRoomId } from '../collab/collab-live-source';
import { tableStore } from '../stores/table-store';
import { shouldEjectOnDeletedVersion } from '../utils';
import { sendProStudioEvent } from '../xstate/app.xstate';

/**
 * PSTUDIO-3671: always-on, backend-anchored detection that the OPEN solution
 * version was deleted — the piece missing from the branch's other triggers.
 *
 * The same-browser fast paths (`useSolutionDeletedSync` /
 * `useCrossTabNodeSync`) ride BroadcastChannel and never cross machines; the
 * `botFlow` load guard only fires on a manual reload; and
 * `useCoworkerFocusSubscription` only subscribes while ANOTHER user holds an
 * active cell focus. So a user who simply has the fully-loaded editor open on a
 * different machine is never told when someone else deletes the solution — they
 * sit on stale data and the next autosave hits the deleted `solutionVersionId`
 * (the PSTUDIO-3669 FK-violation family).
 *
 * This watchdog closes that gap: whenever a solution version is open it holds
 * lightweight subscriptions purely to observe existence — the legacy
 * `solutionVersionSubscription` and, since PSTUDIO-4449, the version row's own
 * `solutionVersionStatusSubscription` (a v2 draft only). Each source's first
 * non-null push arms it; a subsequent null (the
 * deletion push Hasura emits for a removed `by_pk` row) then dispatches
 * `solutionNotFound` (toast + redirect to All Solutions, via the machine's
 * global `#solutionNotFound`). A null that was never preceded by data is a
 * still-provisioning transient — never an eject (see `shouldEjectOnDeletedVersion`).
 *
 * It deliberately does NOT merge payload data — live co-editing stays owned by
 * `useCoworkerFocusSubscription`; this hook only watches whether the version
 * still exists, so a second (cheap) subscription while a coworker is focused is
 * acceptable.
 */
export function useSolutionExistenceSubscription(): void {
  const { solutionVersionId } = tableStore;

  useEffect(() => {
    if (!solutionVersionId) return;

    // Fresh subscriptions for this version: nothing seen yet.
    const seen = { legacy: false, version: false };

    /**
     * One source's push. PSTUDIO-4449: two sources, because since the CRDT
     * cutover the backend no longer writes a v2 draft into
     * `solution_versions_ps` — that subscription answers null from the start
     * and never arms — while the draft's `solution_versions` row still exists
     * and goes away with the solution. Each source arms on its own data and
     * ejects on its own null, so a source that never had the row cannot eject.
     */
    const onPush = (source: keyof typeof seen, versionData: unknown) => {
      // PSTUDIO-3953: this callback races the effect's own cleanup — urql can
      // deliver a message that was already queued when the version changed.
      // The branch below EJECTS the user (toast + redirect), so acting on a
      // late null for a version they have already left would throw them out of
      // the solution they are currently working in. The same gate is on the
      // other two ejecting paths (`reload-nodes`, `useCoworkerFocusSubscription`);
      // this hook is the third and is the ALWAYS-ON deleted-version detector,
      // so leaving it ungated would have kept the defect reachable by the very
      // path most likely to fire.
      if (tableStore.solutionVersionId !== solutionVersionId) return;

      if (
        shouldEjectOnDeletedVersion({
          hasSeenVersionData: seen[source],
          versionData,
        })
      ) {
        sendProStudioEvent({ type: 'solutionNotFound' });
        return;
      }

      if (versionData) {
        seen[source] = true;
      }
    };

    const unsubscribes: Array<() => void> = [];
    try {
      const { subscribe } = proStudioUrqlGql.solutionVersionSubscription({
        solutionVersionId,
      });
      unsubscribes.push(
        subscribe((subData) => {
          if (subData.error) return;
          onPush(
            'legacy',
            subData.data?.tenancy_solution_versions_ps_by_pk ?? null
          );
        }).unsubscribe
      );
    } catch (_err) {
      // A failed subscribe must never break the editor — the load-time guard
      // and the same-browser fast paths still apply.
    }
    // A legacy draft's composite key is no version row's id: that source
    // could only ever answer null, at the cost of a live query per tab.
    if (isCollabRoomId(solutionVersionId)) {
      try {
        const { subscribe } =
          proStudioUrqlGql.solutionVersionStatusSubscription({
            id: solutionVersionId,
          });
        unsubscribes.push(
          subscribe((subData) => {
            if (subData.error) return;
            onPush(
              'version',
              subData.data?.tenancy_solution_versions_by_pk ?? null
            );
          }).unsubscribe
        );
      } catch (_err) {
        // As above.
      }
    }

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [solutionVersionId]);
}
